<template>
    <b-modal v-model="visible" title="View Call Log Audit" @hidden="handleModalHidden" size="lg" scrollable
        hide-footer>
        <template v-if="call && questionSet">
            <div class="mb-3">
                <h5>Call Information</h5>
                <div class="row">
                    <div class="col-md-6">
                        <strong>External Number:</strong> {{ call.externalNumber }}
                    </div>
                    <div class="col-md-6">
                        <strong>Agent:</strong> {{ call.agent }}
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-6">
                        <strong>Date:</strong> {{ formatDate(call.initiated) }}
                    </div>
                    <div class="col-md-6">
                        <strong>Duration:</strong> {{ formatDuration(call.duration) }}
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-6">
                        <strong>Direction:</strong> {{ call.outbound ? 'Outbound' : 'Inbound' }}
                    </div>
                </div>
            </div>

            <hr />

            <div class="mb-3">
                <h5>Audit Form: {{ questionSet.name }}</h5>
                <div v-if="call.auditCompletedBy || call.auditCompletedAt" class="text-muted">
                    <small>
                        <strong>Audit Details:</strong>
                        <span v-if="call.auditCompletedBy">
                            Completed by {{ call.auditCompletedBy }}
                        </span>
                        <span v-if="call.auditCompletedAt">
                            on {{ formatDate(call.auditCompletedAt) }}
                        </span>
                    </small>
                </div>
            </div>

            <!-- Questions in read-only mode -->
            <div>
                <TaskQuestions :questions="questionSet.questions" :answers="filteredAnswers"
                    :disabled="true" :editing="false" :entity-type="EntityType.CallLog" />
            </div>

            <div class="text-center mt-3">
                <b-button variant="secondary" @click="visible = false">Close</b-button>
            </div>
        </template>

        <template v-else-if="loading">
            <div class="text-center">
                <b-spinner />
                <p class="mt-2">Loading audit information...</p>
            </div>
        </template>

        <template v-else>
            <div class="text-center text-muted">
                <p>No audit data available for this call.</p>
                <b-button variant="secondary" @click="visible = false">Close</b-button>
            </div>
        </template>
    </b-modal>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import moment from 'moment';
import { Call } from '@/models/Call';
import { UserTaskQuestionSet, UserTaskAnswer } from '@/models/UserTask';
import { EntityType } from '@/models/EntityType';
import TaskQuestions from '@/components/Tasks/TaskQuestions.vue';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

@Component({
    name: "CallLogAuditViewer",
    components: {
        TaskQuestions
    }
})
export default class CallLogAuditViewer extends Vue {
    EntityType = EntityType;

    @Prop({ required: true }) value!: boolean;
    @Prop({ required: true }) call!: Call | null;

    private questionSet: UserTaskQuestionSet | null = null;
    private auditAnswers: UserTaskAnswer[] = [];
    private loading = false;

    get visible(): boolean {
        return this.value;
    }

    set visible(val: boolean) {
        this.$emit('input', val);
    }

    get filteredAnswers(): UserTaskAnswer[] {
        // Return all answers since we no longer store metadata as answers
        return this.auditAnswers;
    }

    @Watch('visible')
    async onVisibleChange(newVal: boolean) {
        if (newVal && this.call) {
            await this.loadAuditData();
        }
        if (!newVal) {
            this.resetData();
        }
    }

    async loadAuditData() {
        if (!this.call?.id) return;

        this.loading = true;
        try {
            // Load audit answers
            const answersResponse = await this.$http.get<UserTaskAnswer[]>(`/Call/audit-answers/${this.call.id}`);
            this.auditAnswers = answersResponse.data;

            if (this.auditAnswers.length === 0) {
                return;
            }

            // Get question set ID from call metadata (new approach)
            if (this.call.auditQuestionSetId) {
                await this.loadQuestionSetById(this.call.auditQuestionSetId);
            } else {
                // Fall back to searching if no metadata found (legacy audits)
                const firstAnswer = this.auditAnswers.find(a => a.taskQuestionId > 0);
                if (firstAnswer) {
                    await this.findQuestionSetForAnswer(firstAnswer.taskQuestionId);
                }
            }
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error('Failed to load audit data:', err);
        } finally {
            this.loading = false;
        }
    }


    async loadQuestionSetById(questionSetId: number) {
        try {
            const response = await this.$http.get<UserTaskQuestionSet>(`/task/question-set/${questionSetId}`);
            this.questionSet = response.data;
        } catch (err) {
            console.error(`Failed to load question set ${questionSetId}:`, err);
            // Fall back to searching by question ID
            const firstAnswer = this.auditAnswers.find(a => a.taskQuestionId > 0);
            if (firstAnswer) {
                await this.findQuestionSetForAnswer(firstAnswer.taskQuestionId);
            }
        }
    }

    async findQuestionSetForAnswer(questionId: number) {
        try {
            // Load all question sets for CallLog entity type
            const questionSetsResponse = await this.$http.get<UserTaskQuestionSet[]>(`/task/question-sets/${EntityType.CallLog}`);
            const questionSets = questionSetsResponse.data;

            // Try to find the question set containing this question
            for (const qs of questionSets) {
                try {
                    const fullQuestionSet = await this.$http.get<UserTaskQuestionSet>(`/task/question-set/${qs.id}`);
                    if (fullQuestionSet.data.questions?.some(q => q.id === questionId)) {
                        this.questionSet = fullQuestionSet.data;
                        return;
                    }
                } catch (err) {
                    // Skip this question set if there's an error loading it
                    console.warn(`Failed to load question set ${qs.id}:`, err);
                    continue;
                }
            }

            // If we didn't find the question set, it might have been deleted
            console.warn(`Question set containing question ${questionId} not found`);
        } catch (err) {
            console.error('Failed to find question set:', err);
        }
    }

    resetData() {
        this.questionSet = null;
        this.auditAnswers = [];
        this.loading = false;
    }

    formatDate(date: Date | string): string {
        return moment.utc(date).local().format("L LT");
    }

    formatDuration(seconds: number): string {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }

    handleModalHidden() {
        this.resetData();
    }
}
</script>

<style scoped>
.call-info {
    background-color: #f8f9fa;
    padding: 1rem;
    border-radius: 0.25rem;
    margin-bottom: 1rem;
}
</style>